import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import AnalyticsApiService from '../../ApiServices/AnalyticsApiService/AnalyticsApiService';
import { toast } from 'react-toastify';
import PublisherApiService from '../../ApiServices/PublisherApiService/PublisherApiService';
import { useAuth } from "../Auth/AuthContext";
import WebsiteApiService from '../../ApiServices/WebsiteApiService/WebsiteApiService';
import AdunitApiService from '../../ApiServices/AnalyticsApiService/AdunitApiService';
import { BarChart3, Calendar, CreditCard, DollarSign, Eye, Globe, Layout, MapPin, MousePointer, Percent, RefreshCcw, TrendingDown, TrendingUp, User } from 'lucide-react';

const AddAnalyticsForm = ({ isOpen, onRequestClose, onSubmit }) => {
    const [formData, setFormData] = useState({
        date: new Date(),
        publisherId: '',
        publisher: '',
        websiteId: '',
        website: '',
        adUnits: '',
        adExchangeImpressions: '',
        adExchangeClicks: '',
        adExchangeCTR: '',
        adExchangeRevenue: '',
        adExchangePayout: '',
        adExchangeAvgECPM: '',
        adExchangeAdRequests: '',
        adExchangeMatchRate: '',
        adExchangeFillRate: ''
    });


    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [publishersData, setPublishers] = useState([]);
    const [webData, setWebsites] = useState([]);
    const [adunitsData, setadunitsData] = useState([]);
    const [revenuePayout, setrevenuePayout] = useState([]);

    const { authState } = useAuth();
    const user = authState.user;
    const role = authState.roles;
    const admin = role === "Admin";

    useEffect(() => {
        if (admin) {
            fetchPublishers();
            fetchAdunits();
        }
    }, [admin]);

    const fetchPublishers = async () => {
        try {
            const response = await PublisherApiService.get();
            setPublishers(response.data || []);
        } catch (error) {
            toast.error("Failed to load publishers");
        }
    };

    const fetchAdunits = async () => {
        setLoading(true);
        try {
            const response = await AdunitApiService.getAll();
            setadunitsData(response.data || []);
        } catch (error) {
            toast.error('Failed to fetch adunits.');
        } finally {
            setLoading(false);
        }
    };

    const fetchWebsites = async (publisherId) => {
        if (!publisherId) {
            setWebsites([]);
            return;
        }
        try {
            const response = await WebsiteApiService.GetAllWebByUserId(publisherId);
            setWebsites(response.data || []);
        } catch (error) {
            toast.error("Failed to load websites");
        }
    };

    const handlePublisherChange = (e) => {
        const publisherId = e.target.value;
        setFormData((prev) => ({ ...prev, publisher: publisherId, website: '' }));
        fetchWebsites(publisherId);
    };

    const handleInputChange = (e) => {
        if (e.target.name == "adExchangeRevenue") {
            const revenue = e.target.value;
            const payoutDeduction = revenue * 15 / 100; // 15% of revenue
            const payout = revenue - payoutDeduction;
            setrevenuePayout(payout);
        }
        setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleDateChange = (date) => {
        setFormData((prev) => ({ ...prev, date }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const obj = {
                publisherId: formData.publisher,
                publisher: formData.publisher,
                website: formData.website,
                websiteId: formData.website,
                date: formData.date.toISOString(),
                adUnits: formData.adunit,
                adUnit: formData.adunit,
                adExchangeImpressions: formData.adExchangeImpressions,
                adExchangeClicks: formData.adExchangeClicks,
                adExchangeCTR: formData.adExchangeCTR,
                adExchangeRevenue: formData.adExchangeRevenue,
                adExchangePayout: revenuePayout,
                adExchangeAvgECPM: formData.adExchangeAvgECPM,
                adExchangeAdRequests: formData.adExchangeAdRequests,
                adExchangeMatchRate: formData.adExchangeMatchRate,
                adExchangeFillRate: formData.adExchangeFillRate,
                country: formData.country,
            };
            await AnalyticsApiService.addAnalytics(obj);
            toast.success("Analytics data added successfully!");
            onSubmit();
            onRequestClose();

        } catch (error) {
            setError(error.message || 'Failed to add analytics data');
        } finally {
            setLoading(false);
        }
    };


return (
  <Modal
    isOpen={isOpen}
    onRequestClose={onRequestClose}
    className="flex items-center justify-center min-h-screen p-4"
    overlayClassName="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300"
  >
    <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-4xl w-full relative max-h-[90vh] overflow-hidden flex flex-col border border-gray-100">
      {/* Close Button */}
      <button
        onClick={onRequestClose}
        className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1 rounded-full hover:bg-gray-100"
        aria-label="Close modal"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      {/* Header */}
      <div className="text-center mb-6">
        <h2 className="text-3xl font-bold text-gray-800 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
          Add Analytics
        </h2>
        <p className="text-gray-500 mt-2">Fill in the details below to add new analytics data</p>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable Form */}
      <div className="overflow-y-auto flex-grow px-1 py-2" style={{ maxHeight: "65vh" }}>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* First Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Adunit */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Ad Unit</label>
              <div className="relative">
                <select
                  name="adunit"
                  value={formData.adunit}
                  onChange={handleInputChange}
                  className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                >
                  <option value="">Select Ad Unit</option>
                  {adunitsData.map((adunit, index) => (
                    <option key={adunit.id || index} value={adunit.id}>{adunit.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Layout className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Country */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Country</label>
              <div className="relative">
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleInputChange}
                  className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                >
                  <option value="">Select Country</option>
                  {[
                    "United States", "United Kingdom", "Canada", "Australia",
                    "Germany", "France", "India", "China", "Japan",
                    "Brazil", "Mexico", "Italy", "Spain", "Netherlands", "South Korea"
                  ].map((country, index) => (
                    <option key={index} value={country}>{country}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <MapPin className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Publisher */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Publisher</label>
              <div className="relative">
                <select
                  name="publisher"
                  value={formData.publisher}
                  onChange={handlePublisherChange}
                  className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                >
                  <option value="">Select Publisher</option>
                  {publishersData.map((pub, index) => (
                    <option key={pub.id || index} value={pub.id}>{pub.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <User className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Website */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Website</label>
              <div className="relative">
                <select
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="block w-full pl-3 pr-10 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                >
                  <option value="">Select Website</option>
                  {webData.map((site, index) => (
                    <option key={site.websiteId || index} value={site.websiteId}>{site.name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <Globe className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { field: 'adExchangeImpressions', label: 'Impressions', icon: <Eye className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeClicks', label: 'Clicks', icon: <MousePointer className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeCTR', label: 'CTR (%)', icon: <Percent className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeRevenue', label: 'Revenue ($)', icon: <DollarSign className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeAvgECPM', label: 'Avg eCPM ($)', icon: <BarChart3 className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeAdRequests', label: 'Ad Requests', icon: <RefreshCcw className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeMatchRate', label: 'Match Rate (%)', icon: <TrendingUp className="h-5 w-5 text-gray-400" /> },
              { field: 'adExchangeFillRate', label: 'Fill Rate (%)', icon: <TrendingDown className="h-5 w-5 text-gray-400" /> }
            ].map(({ field, label, icon }) => (
              <div key={field} className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">{label}</label>
                <div className="relative">
                  <input
                    name={field}
                    type="number"
                    value={formData[field]}
                    onChange={handleInputChange}
                    className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                    required
                    step={field.includes('Rate') || field.includes('CTR') ? "0.01" : "1"}
                  />
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                    {icon}
                  </div>
                </div>
              </div>
            ))}

            {/* Payout (read-only) */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Payout ($)</label>
              <div className="relative">
                <input
                  name="adExchangePayout"
                  readOnly
                  type="number"
                  value={revenuePayout}
                  className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 bg-gray-50 rounded-lg shadow-sm"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <CreditCard className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>

            {/* Date Picker */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Date</label>
              <div className="relative">
                <DatePicker
                  selected={formData.date}
                  onChange={handleDateChange}
                  className="block w-full pl-10 pr-3 py-2.5 text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 rounded-lg shadow-sm transition-all"
                  required
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Calendar className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={onRequestClose}
              className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </span>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Modal>
);
};

export default AddAnalyticsForm;
